import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Avatar,
  Fade,
  Zoom,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { createPortal } from "react-dom";
import { Badge } from "types/badge";

type BadgeAnimationModalProps = {
  badges: Badge[];
  open: boolean;
  onClose: () => void;
};

// Fireworks particle component
const FireworkParticle: React.FC<{
  x: number;
  y: number;
  color: string;
  delay: number;
  angle: number;
  distance: number;
  index: number;
}> = ({ x, y, color, delay, angle, distance, index }) => {
  const animationName = `firework-explode-${index}`;
  const translateX = Math.cos(angle) * distance;
  const translateY = Math.sin(angle) * distance;
  const duration = 1.5 + Math.random() * 0.5;

  return (
    <>
      <style>
        {`
          @keyframes ${animationName} {
            0% {
              transform: translate(0, 0) scale(1);
              opacity: 1;
            }
            100% {
              transform: translate(${translateX}px, ${translateY}px) scale(0);
              opacity: 0;
            }
          }
        `}
      </style>
      <Box
        sx={{
          position: "absolute",
          left: `${x}%`,
          top: `${y}%`,
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: color,
          boxShadow: `0 0 10px ${color}, 0 0 20px ${color}`,
          animation: `${animationName} ${duration}s ease-out ${delay}s forwards`,
        }}
      />
    </>
  );
};

// Firework burst component
const FireworkBurst: React.FC<{
  x: number;
  y: number;
  colors: string[];
  delay: number;
  burstIndex: number;
}> = ({ x, y, colors, delay, burstIndex }) => {
  const particles = useMemo(() => {
    const count = 20 + Math.floor(Math.random() * 10);
    return Array.from({ length: count }, (_, i) => {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
      const distance = 100 + Math.random() * 150;
      const color = colors[Math.floor(Math.random() * colors.length)];
      return { angle, distance, color };
    });
  }, []);

  return (
    <Box
      sx={{
        position: "absolute",
        left: `${x}%`,
        top: `${y}%`,
        width: 0,
        height: 0,
      }}
    >
      {particles.map((particle, index) => (
        <FireworkParticle
          key={`${burstIndex}-${index}`}
          x={0}
          y={0}
          color={particle.color}
          delay={delay}
          angle={particle.angle}
          distance={particle.distance}
          index={burstIndex * 1000 + index}
        />
      ))}
    </Box>
  );
};

export const BadgeAnimationModal: React.FC<BadgeAnimationModalProps> = ({
  badges,
  open,
  onClose,
}) => {
  const [showContent, setShowContent] = useState(false);
  const [fireworks, setFireworks] = useState<Array<{ x: number; y: number; delay: number }>>([]);

  useEffect(() => {
    if (open && badges && badges.length > 0) {
      // Small delay before showing content for animation effect
      const timer = setTimeout(() => {
        setShowContent(true);
      }, 300);

      // Generate multiple firework bursts at random positions
      const fireworkBursts = Array.from({ length: 8 }, (_, i) => ({
        x: 10 + Math.random() * 80, // Random x position (10% to 90%)
        y: 10 + Math.random() * 80, // Random y position (10% to 90%)
        delay: i * 0.2, // Stagger the bursts
      }));
      setFireworks(fireworkBursts);

      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
      setFireworks([]);
    }
  }, [open, badges]);

  if (!badges || badges.length === 0 || !open) return null;

  const fireworkColors = [
    "#FF6B6B", // Red
    "#4ECDC4", // Teal
    "#45B7D1", // Blue
    "#FFA07A", // Light Salmon
    "#98D8C8", // Mint
    "#F7DC6F", // Yellow
    "#BB8FCE", // Purple
    "#85C1E2", // Sky Blue
  ];

  const backdropContent = (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        color: "#fff",
        zIndex: 9999, // Very high z-index to be above everything
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
    >
      <IconButton
        aria-label="Close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          zIndex: 10,
          color: "white",
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.25)",
          },
          width: 48,
          height: 48,
        }}
      >
        <Close sx={{ fontSize: 28 }} />
      </IconButton>
      {/* Fireworks */}
      {fireworks.map((firework, index) => (
        <FireworkBurst
          key={index}
          x={firework.x}
          y={firework.y}
          colors={fireworkColors}
          delay={firework.delay}
          burstIndex={index}
        />
      ))}
      <Fade in={showContent} timeout={500}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            textAlign: "center",
            maxWidth: 500,
            px: 3,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Zoom in={showContent} timeout={800} style={{ transitionDelay: "200ms" }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: "center",
                mb: 2,
              }}
            >
              {badges.map((badge, index) => (
                <Avatar
                  key={badge.badge_id || index}
                  src={badge.icon_url || undefined}
                  alt={badge.name}
                  sx={{
                    width: 120,
                    height: 120,
                    border: "4px solid",
                    borderColor: "primary.main",
                    backgroundColor: "background.paper",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                  }}
                >
                  {badge.name?.charAt(0) || "?"}
                </Avatar>
              ))}
            </Box>
          </Zoom>
          <Fade in={showContent} timeout={600} style={{ transitionDelay: "400ms" }}>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: "primary.main",
                  textShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                }}
              >
                Congratulations!
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {badges.length === 1
                  ? "You earned a new badge!"
                  : `You earned ${badges.length} new badges!`}
              </Typography>
              {badges.map((badge, index) => (
                <Box key={badge.badge_id || index} sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5, color: "#fff" }}>
                    {badge.name}
                  </Typography>
                  {badge.description && (
                    <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
                      {badge.description}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Fade>
        </Box>
      </Fade>
    </Box>
  );

  // Render using portal to ensure it's at the root level
  return typeof document !== "undefined"
    ? createPortal(backdropContent, document.body)
    : null;
};
